import { Flex } from '@chakra-ui/layout';
import { Button, Card, CardBody, CardHeader } from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useSimpleColorMode } from 'packages/common-frontend/theme/utils';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';
import { RequiredTasksAlert } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/required-tasks/RequiredTasksAlert';

type FacilityDetailCardProps = {
  title: string;
  to: string;
  children: React.ReactNode;
};

const FacilityDetailCard = ({
  title,
  to,
  children,
}: FacilityDetailCardProps) => {
  const mode = useSimpleColorMode();

  const flags = useFeatureFlags();

  return (
    <Card width="20rem" variant="outline">
      <Button
        isDisabled={flags.productsImplementationPortalPermissions !== 'allow'}
        as={Link}
        colorScheme="v2-aurora"
        variant="link"
        to={
          flags.productsImplementationPortalPermissions === 'allow'
            ? to
            : undefined
        }
        textDecoration="none"
        height="100%"
        _hover={{
          textDecoration: 'none',
          background: mode(colors.gray[50], colors.gray[800]),
        }}
        _disabled={{
          cursor: 'not-allowed',
          opacity: 0.5,
        }}
      >
        <Flex direction="column" height="100%">
          <CardHeader justifyContent="flex-start">
            <Typography variant="section-title" trim>
              {title}
            </Typography>
          </CardHeader>
          <CardBody>
            <Typography variant="base-muted" TextProps={{ whiteSpace: 'wrap' }}>
              {children}
            </Typography>
          </CardBody>
        </Flex>
      </Button>
    </Card>
  );
};

export const FacilityDetails = () => {
  const params = useParams();
  const { facilityId } = FacilityDetailsRouteParamsStruct.parse(params);

  const facility = useCreditFacilityOrThrow(facilityId);

  return (
    <Flex direction="column" gap="1.5rem">
      <Flex justifyContent="space-between" alignItems="center" pb="1.5rem">
        <Typography variant="page-title" trim>
          {facility.name}
        </Typography>
      </Flex>
      <RequiredTasksAlert />
      <Flex gap="1.5rem" wrap="wrap">
        <FacilityDetailCard title="Facility" to="facility-mechanics">
          Defines the loan structure, financial commitment, key terms, and fund
          management framework.
        </FacilityDetailCard>
        <FacilityDetailCard title="Parties" to="parties">
          Lists key participants responsible for executing, managing, and
          ensuring compliance with the loan agreement.
        </FacilityDetailCard>
        <FacilityDetailCard title="Pricing Options" to="pricing-options">
          Configurable pricing structure applied across commitments to inform
          loan management and loan creation.
        </FacilityDetailCard>
        <FacilityDetailCard title="Commitments" to="commitments">
          Specifies lenders&apos; funding obligations under the facility,
          ensuring capital availability and structured execution.
        </FacilityDetailCard>
        <FacilityDetailCard title="Expenses" to="expense-mechanics">
          Defines fees in the waterfall, including structure, payment rules,
          allocation, and administration requirements.
        </FacilityDetailCard>
      </Flex>
    </Flex>
  );
};
